import type {ReactElement} from 'react';
import {useCallback, useMemo, useState} from 'react';
import {useLatest} from 'ahooks';
import styles from './ai-prompt-box-attachments.module.scss';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {useDesignAttachmentScreenURLs} from '@Components/ai-prompt-box/hooks/useDesignAttachmentScreenURLs';
import type {AiPromptBoxAttachmentsProps, PromptAttachment} from './ai-prompt-box-attachments.types';
import AiPromptBoxAttachmentPreview from '@Components/ai-prompt-box/components/ai-prompt-box-attachment-preview/ai-prompt-box-attachment-preview';
import {AiPromptBoxLoadingAttachmentPreview} from '@Components/ai-prompt-box/components/ai-prompt-box-loading-attachment-preview';
import {useAiPromptBoxDragDropUploader} from '@Components/ai-prompt-box/hooks/useAiPromptBoxDragDropUploader';
import {useAttachmentLightbox} from '@Components/ai-prompt-box/hooks/useAttachmentLightbox';
import {useAttachmentScrollIndicator} from '@Components/ai-prompt-box/hooks/useAttachmentScrollIndicator';
import {ZoomableLightbox} from '@Components/zoomable-lightbox/zoomable-lightbox';
import {
  convertPromptAttachmentsToImageData,
  getAttachmentPreviewUrl,
  getAttachmentUniqueKey,
  LOADING_ATTACHMENT_CANCELLED_EVENT,
} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {clearPendingUpload, getAttachmentPendingUpload, isPendingImageAttachment, isUserImageAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';

export function AiPromptBoxAttachments(props: AiPromptBoxAttachmentsProps): ReactElement | null {
  const {state, actions} = useAiPromptBox(props.id);
  const attachments = state.attachments?.items ?? [];

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const isScrolling = useAttachmentScrollIndicator(containerEl);

  const stateRef = useLatest(state);
  const {designScreenURLs, prefetchDesignScreenURLAtIndex} = useDesignAttachmentScreenURLs(attachments);
  const {retryUpload} = useAiPromptBoxDragDropUploader(props.id);
  const lightbox = useAttachmentLightbox(attachments.length, prefetchDesignScreenURLAtIndex);

  const handleRemoveAttachment = useCallback((index: number): void => {
    const currentState = stateRef.current;
    const items = currentState.attachments?.items ?? [];
    const target = items[index];

    if (target && isPendingImageAttachment(target)) {
      clearPendingUpload(props.id, target.localId);
    }

    const updatedItems = items.filter((_, i) => i !== index);
    actions.update({
      attachments: {
        items: updatedItems,
        dropdown: currentState.attachments?.dropdown ?? [],
      },
      attachmentLoaders: currentState.attachmentLoaders ?? [],
    });
  }, [actions, props.id, stateRef]);

  const handleRemoveLoadingAttachment = (id: string): void => {
    actions.removeLoadingAttachments([id]);
    window.dispatchEvent(new CustomEvent(LOADING_ATTACHMENT_CANCELLED_EVENT, {detail: {id}}));
  };

  const handleRetryAttachment = useCallback((attachmentIndex: number): void => {
    const items = stateRef.current.attachments?.items ?? [];
    const target = items[attachmentIndex];
    if (target && isPendingImageAttachment(target)) {
      retryUpload(target.localId);
    }
  }, [stateRef, retryUpload]);

  const imagesAttachmentData = useMemo(() => {
    return convertPromptAttachmentsToImageData(attachments, false, designScreenURLs);
  }, [attachments, designScreenURLs]);

  const isRemovalLocked = state.isFinishingUploads ?? false;

  const getAttachmentPreview = (attachment: PromptAttachment, index: number): ReactElement => {
    const previewUrl = getAttachmentPreviewUrl(attachment);
    const uniqueKey = getAttachmentUniqueKey(attachment);
    const pendingUpload = getAttachmentPendingUpload(attachment);

    return (
      <AiPromptBoxAttachmentPreview
        key={uniqueKey}
        previewUrl={previewUrl}
        index={index}
        onRemove={handleRemoveAttachment}
        onPreviewClick={lightbox.open}
        onRetry={handleRetryAttachment}
        size={state.size}
        pendingUpload={pendingUpload}
        isRemovalLocked={isRemovalLocked || (isUserImageAttachment(attachment) && attachment.isRemovable === false)}
      />
    );
  };

  const getLightboxHeader = (): ReactElement => {
    return (
      <div className={styles.lightboxHeader}>
        <Icon icon="icon-arrow-left" size={IconSize.SIZE_ICON_16} type={IconType.DARK_GHOST} shape={IconShape.SQUARE} onClick={lightbox.close} />
      </div>
    );
  };

  const getAttachments = (): ReactElement | null => {
    const loadingAttachments = state.attachmentLoaders ?? [];

    if (attachments.length === 0 && loadingAttachments.length === 0) {
      return null;
    }

    return (
      <>
        <div ref={setContainerEl} className={`${styles.attachments} ${isScrolling ? styles.isScrolling : ''}`}>
          {loadingAttachments.map((loadingAttachment) => (
            <AiPromptBoxLoadingAttachmentPreview key={loadingAttachment.id} id={loadingAttachment.id} size={state.size} onRemove={handleRemoveLoadingAttachment} />
          ))}
          {attachments.map((attachment, index) => getAttachmentPreview(attachment, index))}
        </div>
        <ZoomableLightbox
          isOpen={lightbox.isOpen}
          onClose={lightbox.close}
          imagesAttachmentData={imagesAttachmentData}
          currentImageIndex={lightbox.currentIndex}
          onPreviousImage={lightbox.goToPrevious}
          onNextImage={lightbox.goToNext}
          headerContent={getLightboxHeader()}
        />
      </>
    );
  };

  return getAttachments();
}
